import {Component, inject, input, OnDestroy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatMenu, MatMenuModule} from '@angular/material/menu';
import {MatSelectModule} from '@angular/material/select';
import {Router} from '@angular/router';
import {CovIconComponent, CovToastService, IconName} from '@cov/ui-angular';
import {translate, TranslocoPipe} from '@jsverse/transloco';
import {EMPTY, Subject, switchMap, take, takeUntil} from 'rxjs';
import {CONTACTS, NOTIFICATION_SETTINGS, PREFERENCES} from '../../../shared/constants';
import {SessionState} from '../../enum/session-state.enum';
import {AsmService} from '../../services/asm.service';
import {SessionService} from '../../services/session.service';
import {UserService} from '../../services/user.service';
import {AuthService} from '../../services/auth.service';
import {LocaleService} from '../../services/locale.service';
import {Language} from '../../../enums/language.enum';
import {NativeLanguageNamePipe} from '../../pipes/native-language-name.pipe';

@Component({
  selector: 'cov-user-preferences',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatMenuModule,
    TranslocoPipe,
    CovIconComponent,
    NativeLanguageNamePipe,
  ],
  templateUrl: './user-preferences.component.html',
  styleUrl: './user-preferences.component.scss',
})
export class UserPreferencesComponent implements OnDestroy {
  // CONST
  protected readonly SESSIONS = SessionState;
  protected readonly IconName = IconName;

  // INPUT
  public menu = input.required<MatMenu>();

  // SERVICE
  protected authService = inject(AuthService);
  protected localeService = inject(LocaleService);
  protected userService = inject(UserService);
  protected toastService = inject(CovToastService);
  protected router = inject(Router);
  protected asmService = inject(AsmService);
  protected sessionService = inject(SessionService);

  // STATE
  protected supportedLanguages: Language[] = Object.values(Language) as Language[];
  protected language = this.localeService.language.asReadonly();
  protected currentUser = this.userService.currentUser;
  protected asmUser = this.asmService.asmUser;
  protected sessionStates = this.sessionService.sessionStates;
  protected destroyed$ = new Subject<void>();

  // METHODS
  changeLang(language: Language) {
    const isocode = this.localeService.toSAPLanguage(language);

    this.asmService
      .getAsmAgent()
      .pipe(
        take(1),
        takeUntil(this.destroyed$),
        switchMap((asmAgent) => {
          if (asmAgent) {
            this.localeService.setLocalAsmLanguage(language);
            window.location.reload(); // Can be removed with CFE-1400
            return EMPTY;
          }
          return this.userService.updateLanguage(isocode).pipe(take(1));
        }),
      )
      .subscribe({
        next: () => {
          // Apply the language locally first so the selector reflects the choice
          // immediately, mirroring the shared React component library.
          this.localeService.changeLanguage(language);
          this.toastService.success(translate('preferences.preferencesUpdated'));
          // Reload must wait for the localStorage write inside changeLanguage to land —
          // otherwise LocaleService re-reads the stale stored language on the reloaded page.
          this.localeService
            .isReady()
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe(() => window.location.reload()); // Can be removed with CFE-1400
        },
        error: () => {
          this.toastService.error(translate('preferences.preferencesUpdateFailed'));
        },
      });
  }

  goToContacts() {
    this.router.navigate([CONTACTS]);
    this.menu().closed.emit('click');
  }

  goToPreferences() {
    this.router.navigate([PREFERENCES]);
    this.menu().closed.emit('click');
  }

  goToNotificationSettings() {
    this.router.navigate([NOTIFICATION_SETTINGS]);
    this.menu().closed.emit('click');
  }

  logout() {
    this.menu().closed.emit('click');
    this.authService.logout();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
